import React, { useState, useEffect } from 'react';
import { ref, update, onValue } from 'firebase/database';
import { db } from './firebase';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = { monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday' };

const DEFAULT_HOURS = {
  monday: { open: true, from: '08:00', to: '20:00' },
  tuesday: { open: true, from: '08:00', to: '20:00' },
  wednesday: { open: true, from: '08:00', to: '20:00' },
  thursday: { open: true, from: '08:00', to: '20:00' },
  friday: { open: true, from: '08:00', to: '20:00' },
  saturday: { open: true, from: '09:00', to: '18:00' },
  sunday: { open: false, from: '10:00', to: '14:00' },
};

export default function WorkingHoursTab({ pharmacyId }) {
  const [hours, setHours] = useState(DEFAULT_HOURS);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    onValue(ref(db, `pharmacies/${pharmacyId}/workingHours`), snap => {
      if (snap.exists()) setHours({ ...DEFAULT_HOURS, ...snap.val() });
    });
  }, [pharmacyId]);

  const updateDay = (day, field, value) => {
    setHours(prev => ({ ...prev, [day]: { ...prev[day], [field]: value } }));
    setSaved(false);
  };

  const applyToWeekdays = () => {
    const weekdayHours = { from: hours.monday.from, to: hours.monday.to, open: true };
    const updated = { ...hours };
    ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].forEach(d => {
      updated[d] = { ...weekdayHours };
    });
    setHours(updated);
    setSaved(false);
  };

  const saveHours = async () => {
    setSaving(true);
    try {
      await update(ref(db, `pharmacies/${pharmacyId}`), { workingHours: hours });
      await update(ref(db, `pharmacies_map/${pharmacyId}`), { workingHours: hours });
      setSaved(true);
      alert('✅ Working hours saved! Customers will see accurate open/close times on the map.');
    } catch { alert('Error saving. Please try again.'); }
    finally { setSaving(false); }
  };

  const TIME_OPTIONS = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const hh = String(h).padStart(2, '0');
      const mm = String(m).padStart(2, '0');
      const label = h < 12 ? `${h === 0 ? 12 : h}:${mm} AM` : `${h === 12 ? 12 : h - 12}:${mm} PM`;
      TIME_OPTIONS.push({ value: `${hh}:${mm}`, label });
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontWeight: 700, margin: 0, fontSize: 18 }}>🕐 Working Hours</h2>
          <p style={{ color: '#888', fontSize: 13, margin: '4px 0 0' }}>
            Set your opening hours — customers see live open/closed status on the map
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            style={{ padding: '9px 16px', backgroundColor: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
            onClick={applyToWeekdays}
          >
            📋 Copy Mon to all weekdays
          </button>
          <button
            style={{ padding: '9px 20px', backgroundColor: saving ? '#A8D5C2' : '#1D9E75', color: '#FFF', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}
            onClick={saveHours}
            disabled={saving}
          >
            {saving ? 'Saving...' : saved ? '✅ Saved' : '✓ Save Hours'}
          </button>
        </div>
      </div>

      {/* INFO */}
      <div style={{ backgroundColor: '#E8F8F2', borderRadius: 10, padding: 14, marginBottom: 20, border: '1px solid #B2DFD0' }}>
        <p style={{ margin: 0, fontSize: 13, color: '#1D6B52' }}>
          💡 Your working hours are shown on the customer map in real time. When you are outside your set hours the map shows your pharmacy as <strong>Closed</strong> automatically — no manual toggle needed.
        </p>
      </div>

      {/* DAYS */}
      <div style={{ backgroundColor: '#FFF', borderRadius: 12, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
        {DAYS.map((day, i) => (
          <div key={day} style={{
            display: 'flex', alignItems: 'center', padding: '16px 20px', gap: 16,
            borderBottom: i < DAYS.length - 1 ? '1px solid #F3F4F6' : 'none',
            backgroundColor: hours[day]?.open ? '#FFF' : '#F9FAFB',
          }}>

            {/* OPEN TOGGLE */}
            <div style={{ width: 100, display: 'flex', alignItems: 'center', gap: 10 }}>
              <input
                type="checkbox"
                checked={hours[day]?.open || false}
                onChange={e => updateDay(day, 'open', e.target.checked)}
                style={{ width: 18, height: 18, cursor: 'pointer', accentColor: '#1D9E75' }}
              />
              <span style={{
                fontSize: 14, fontWeight: 600,
                color: hours[day]?.open ? '#1A1A1A' : '#9CA3AF',
              }}>
                {DAY_LABELS[day]}
              </span>
            </div>

            {hours[day]?.open ? (
              <>
                {/* FROM */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ fontSize: 13, color: '#888', whiteSpace: 'nowrap' }}>Opens at</label>
                  <select
                    style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 13, backgroundColor: '#F7F8FA', outline: 'none', color: '#222' }}
                    value={hours[day]?.from || '08:00'}
                    onChange={e => updateDay(day, 'from', e.target.value)}
                  >
                    {TIME_OPTIONS.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>

                {/* TO */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ fontSize: 13, color: '#888', whiteSpace: 'nowrap' }}>Closes at</label>
                  <select
                    style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 13, backgroundColor: '#F7F8FA', outline: 'none', color: '#222' }}
                    value={hours[day]?.to || '20:00'}
                    onChange={e => updateDay(day, 'to', e.target.value)}
                  >
                    {TIME_OPTIONS.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>

                {/* DURATION */}
                <div style={{ marginLeft: 'auto' }}>
                  {(() => {
                    const [fh, fm] = (hours[day]?.from || '08:00').split(':').map(Number);
                    const [th, tm] = (hours[day]?.to || '20:00').split(':').map(Number);
                    const diff = (th * 60 + tm) - (fh * 60 + fm);
                    if (diff <= 0) return <span style={{ fontSize: 12, color: '#EF4444' }}>⚠️ Invalid</span>;
                    const hrs = Math.floor(diff / 60);
                    const mins = diff % 60;
                    return (
                      <span style={{ fontSize: 12, color: '#888', backgroundColor: '#F7F8FA', padding: '4px 10px', borderRadius: 20 }}>
                        {hrs}h{mins > 0 ? ` ${mins}m` : ''} open
                      </span>
                    );
                  })()}
                </div>
              </>
            ) : (
              <span style={{ fontSize: 13, color: '#9CA3AF', fontStyle: 'italic' }}>
                Closed all day
              </span>
            )}
          </div>
        ))}
      </div>

      {/* PREVIEW */}
      <div style={{ backgroundColor: '#FFF', borderRadius: 12, padding: 20, marginTop: 20, border: '1px solid #E5E7EB' }}>
        <h3 style={{ fontWeight: 700, marginBottom: 14, fontSize: 15 }}>📅 Weekly Schedule Preview</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8 }}>
          {DAYS.map(day => (
            <div key={day} style={{ textAlign: 'center' }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: '#888', margin: '0 0 6px', textTransform: 'uppercase' }}>
                {day.slice(0, 3)}
              </p>
              <div style={{
                backgroundColor: hours[day]?.open ? '#E8F8F2' : '#F9FAFB',
                borderRadius: 8, padding: '8px 4px',
                border: `1px solid ${hours[day]?.open ? '#B2DFD0' : '#E5E7EB'}`,
              }}>
                {hours[day]?.open ? (
                  <>
                    <p style={{ fontSize: 10, color: '#1D9E75', fontWeight: 600, margin: 0 }}>
                      {hours[day]?.from}
                    </p>
                    <p style={{ fontSize: 9, color: '#888', margin: '2px 0' }}>to</p>
                    <p style={{ fontSize: 10, color: '#1D9E75', fontWeight: 600, margin: 0 }}>
                      {hours[day]?.to}
                    </p>
                  </>
                ) : (
                  <p style={{ fontSize: 10, color: '#9CA3AF', fontWeight: 500, margin: 0 }}>Closed</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}